import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheckCircle, faLockAlt } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { isTypeFn } from '@sorare/core/src/gql';
import { RewardType, SimpleReward } from '@sorare/core/src/lib/rewards';

import { ClaimObjectiveInlineButton } from 'components/leaderboard/WarmupRewards/ClaimObjectiveInlineButton';
import { RewardOverview } from 'components/rewards/RewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';

import { Objectives_so5Leaderboard } from './__generated__/index.graphql';

const Description = styled(BodyS)`
  .locked & {
    opacity: 0.5;
  }
`;
const Rewards = styled.div`
  .locked & {
    opacity: 0.5;
  }
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(min-content, calc(6 * var(--unit)))
  );
  gap: var(--unit);
`;

type Props = { so5Leaderboard: Objectives_so5Leaderboard };

export const Objectives = ({ so5Leaderboard }: Props) => {
  const managerProgressionTasks = so5Leaderboard.tasks.filter(
    isTypeFn('ManagerProgressionTask')
  );

  if (!managerProgressionTasks?.length) {
    return null;
  }

  return (
    <Vertical gap={2}>
      {managerProgressionTasks.map((task, index) => {
        const previousTask = managerProgressionTasks[index - 1];
        const simpleRewards = task.configRewards
          .filter(isTypeFn('ProbabilisticBundleConfigReward'))
          .map((configReward): SimpleReward => {
            const shards = configReward.probabilisticBundleConfig.slots
              .at(0)
              ?.probableConfigs.at(0)
              ?.cardShards.at(0);

            return {
              type: RewardType.SHARD,
              count: shards?.quantity ?? 0,
              rarity: shards?.rarity || null,
            };
          });
        const locked =
          previousTask?.aasmState === ManagerProgressionTaskState.READY;

        return (
          <SBHorizontal key={task.id} className={classNames({ locked })}>
            <Horizontal>
              <FontAwesomeIcon
                icon={locked ? faLockAlt : faCheckCircle}
                color={
                  [
                    ManagerProgressionTaskState.COMPLETED,
                    ManagerProgressionTaskState.CLAIMED,
                  ].includes(task.aasmState)
                    ? 'var(--c-score-high)'
                    : 'var(--c-neutral-600)'
                }
              />
              <Description>{task.description}</Description>
              <ClaimObjectiveInlineButton managerProgressionTask={task} />
            </Horizontal>
            <Rewards>
              {simpleRewards.map((simpleReward, i) => (
                <RewardOverview
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  variant={Variant.CLASSIC}
                  {...simpleReward}
                />
              ))}
            </Rewards>
          </SBHorizontal>
        );
      })}
    </Vertical>
  );
};

Objectives.fragments = {
  so5Leaderboard: gql`
    fragment Objectives_so5Leaderboard on So5Leaderboard {
      slug
      tasks {
        ... on ManagerProgressionTask {
          id
          name
          aasmState
          description
          configRewards {
            ... on ProbabilisticBundleConfigReward {
              probabilisticBundleConfig {
                id
                slots {
                  probableConfigs {
                    cardShards {
                      quantity
                      rarity
                    }
                  }
                }
              }
            }
          }
          ...ClaimObjectiveInlineButton_managerProgressionTask
        }
      }
    }
    ${ClaimObjectiveInlineButton.fragments.managerProgressionTask}
  ` as TypedDocumentNode<Objectives_so5Leaderboard>,
};
